import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { take } from 'rxjs';

@Component({
  selector: 'app-screenshot-viewer',
  templateUrl: './screenshot-viewer.component.html',
  styleUrls: ['./screenshot-viewer.component.scss'],
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: { class: 'screenshot-viewer' },
})
export class ScreenshotViewerComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() set images(value: { id: string; url: string; name: string }[]) {
    const newImages = value || [];
    const newIds = newImages.map((i) => i.id);
    const oldIds = this._images.map((i) => i.id);
    const imagesChanged =
      newIds.length !== oldIds.length || newIds.some((id, i) => id !== oldIds[i]);
    this._images = newImages;
    this.imageUrls = newImages.map((i) => i.url);
    this.imageNames = newImages.map((i) => i.name);
    if (imagesChanged) {
      this.imageLoaded = false;
      this.activePdfIndex = -1;
    }
  }
  @Input() currentImage = 0;

  @Output() currentImageChanged = new EventEmitter<number>();
  @Output() download = new EventEmitter<{ id: string; url: string; name: string }>();
  @Output() closeGallery = new EventEmitter<boolean>();

  public imageLoaded = false;
  public imageUrls: string[] = [];
  public imageNames: string[] = [];
  public pdfBlobUrl: SafeResourceUrl | null = null;
  public pdfLoading = false;
  private _images: { id: string; url: string; name: string }[] = [];
  private currentBlobUrl: string | null = null;
  private activePdfIndex = -1;
  private initializingGalleria = false;

  constructor(private sanitizer: DomSanitizer, private http: HttpClient) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentImage']) {
      this.imageLoaded = false;
    }
    if (changes['visible']) {
      if (this.visible) {
        this.initializingGalleria = true;
        setTimeout(() => (this.initializingGalleria = false));
      } else {
        this.cleanupBlobUrl();
        this.activePdfIndex = -1;
        this.imageLoaded = false;
      }
    }
    if (changes['currentImage'] || changes['visible'] || changes['images']) {
      if (this.visible) {
        this.loadPdfIfNeeded();
      }
    }
  }

  onActiveIndexChange(index: number): void {
    if (this.initializingGalleria || index === this.currentImage) return;
    this.currentImage = index;
    this.imageLoaded = false;
    this.loadPdfIfNeeded();
    this.currentImageChanged.emit(index);
  }

  isPdf(index: number): boolean {
    const name = this.imageNames[index] ?? '';
    return name.toLowerCase().endsWith('.pdf');
  }

  downloadCurrentImage(): void {
    const current = this._images[this.currentImage];
    if (!current) return;
    this.download.emit(current);
  }

  close($event: boolean): void {
    this.closeGallery.emit($event);
  }

  ngOnDestroy(): void {
    this.cleanupBlobUrl();
  }

  private loadPdfIfNeeded(): void {
    if (!this.isPdf(this.currentImage)) {
      if (this.activePdfIndex >= 0) {
        this.cleanupBlobUrl();
        this.activePdfIndex = -1;
      }
      return;
    }

    if (this.activePdfIndex === this.currentImage) return;

    this.cleanupBlobUrl();
    this.activePdfIndex = this.currentImage;
    this.pdfLoading = true;

    const pdfIndex = this.currentImage;
    const url = this.imageUrls[this.currentImage];
    this.http.get(url, { responseType: 'arraybuffer' })
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if (this.activePdfIndex !== pdfIndex) return;
          const blob = new Blob([data], { type: 'application/pdf' });
          this.currentBlobUrl = URL.createObjectURL(blob);
          this.pdfBlobUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.currentBlobUrl);
          this.pdfLoading = false;
        },
        error: () => {
          this.pdfLoading = false;
          this.pdfBlobUrl = null;
          this.activePdfIndex = -1;
        }
      });
  }

  private cleanupBlobUrl(): void {
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
      this.currentBlobUrl = null;
    }
    this.pdfBlobUrl = null;
    this.pdfLoading = false;
  }
}
